import { Flex, Typography } from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import { styled } from 'styled-components'
import { DesignSystem } from '~/core/designSystem'
import { OrganizationType } from '~/features/organization/common'
import {
  OrganizationMetricsSummary,
  OrganizationMetricsWeeklySummaries,
} from '~/features/organizationMetrics/common/types'
import { OrganizationMetricsModal } from '../MetricsModal'
import { useData } from './hooks/useData'

type Props = {
  organization: OrganizationType
}

const StyledAreaChart = styled(AreaChart)`
  svg {
    outline: none;
  }
`

export const OrganizationMetricsOverview: React.FC<Props> = ({
  organization,
}) => {
  const designSystem = DesignSystem.use()
  const [isModalOpen, setModalOpen] = useState(false)
  const { metrics } = useData({ organization })

  const summary = metrics?.summary as OrganizationMetricsSummary
  const weeklySummaries =
    metrics?.weeklySummaries as OrganizationMetricsWeeklySummaries

  const data =
    Object.entries(weeklySummaries ?? {}).map(([week, summary]) => ({
      week,
      timeSaved: summary.timeSaved,
    })) ?? []

  const firstWeek = data?.[0]?.week

  const weekBeforeFirst = dayjs(firstWeek)
    .subtract(1, 'week')
    .format('YYYY-MM-DD')

  data.unshift({
    week: weekBeforeFirst,
    timeSaved: 0,
  })

  if (!summary?.timeSaved || summary?.timeSaved < 3) {
    return
  }

  return (
    <Flex
      vertical
      align="center"
      className="w-full"
      style={{ position: 'relative' }}
    >
      {designSystem.isMobile ? (
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            right: 12,
          }}
        >
          <Flex vertical gap={0} align="flex-end">
            <Typography.Text style={{ fontSize: 24 }}>
              {summary?.timeSaved.toFixed(0)}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              hours saved this month
            </Typography.Text>
          </Flex>
        </div>
      ) : (
        <div
          style={{
            position: 'absolute',
            bottom: 24,
            left: 24,
          }}
        >
          <Flex vertical gap={0}>
            <Typography.Text style={{ fontSize: 24 }}>
              {summary?.timeSaved.toFixed(0)} hours
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              saved this month
            </Typography.Text>
          </Flex>
        </div>
      )}

      <ResponsiveContainer width="100%" height={150}>
        <StyledAreaChart
          height={150}
          data={data}
          style={{ cursor: 'pointer' }}
          onClick={() => setModalOpen(!isModalOpen)}
        >
          <XAxis dataKey="week" hide />
          <YAxis hide />
          <defs>
            <linearGradient id="timeSavedGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#f5c650" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#fbecca" stopOpacity={0.2} />
            </linearGradient>
          </defs>

          <Area
            type="monotone"
            dataKey="timeSaved"
            stackId="1"
            stroke="url(#timeSavedGradient)"
            fill="url(#timeSavedGradient)"
            name="Time Saved"
            dot={false}
            activeDot={false}
          />
        </StyledAreaChart>
      </ResponsiveContainer>
      <OrganizationMetricsModal
        open={isModalOpen}
        onCancel={() => setModalOpen(false)}
        organizationMetrics={metrics}
      />
    </Flex>
  )
}
